/**
 * Centralized handlers for the global TanStack Query error caches.
 *
 * Why this lives outside main.tsx:
 *   1. main.tsx wires React, the router, and the query client — bootstrap code.
 *      It cannot be loaded in unit tests without dragging the whole app in.
 *   2. Error handling is the most critical UX path in the app — every failed
 *      mutation/query routes through it. Pinning the contract with focused
 *      unit tests is mandatory.
 *
 * Contract:
 *   - When the backend already recorded an error and surfaced its errorLogId
 *     in the response (ApiError.backendErrorLogId), we DO NOT POST another
 *     /admin/frontend-error mirror. We toast once and link straight to the
 *     backend entry that has the real Java stack.
 *   - When backendErrorLogId is missing (network failure, JS error, legacy
 *     handler), we fall back to reportApiError / reportJavaScriptError and
 *     toast with a link to the resulting frontend record (or the list page).
 *   - 401 (unauthenticated) → no toast (handled by handleUnauthorized which
 *     redirects to login).
 *   - 409 with an "impact" body → no toast (a confirmation dialog handles it).
 */

import {ApiError} from '@/api/client'
import {reportApiError, reportJavaScriptError} from '@/api/error-reporter'
import {routes} from '@/lib/routes'

export interface ToastInput {
    description: string
    errorLogUrl: string
}

export interface ErrorHandlerDeps {
    /** Tag used to flag a 409 body that requires confirmation; suppresses the toast. */
    isImpactBody: (errorDetail?: string) => boolean
    /** Sends the toast — unit tests inject a spy. */
    toast: (input: ToastInput) => void
    /** Maps a generic message ('Failed to fetch') to a localized form. */
    serverUnavailableMessage: () => string
}

/** Returns true when no toast should be shown for this error. */
function shouldSuppressToast(error: Error, isImpactBody: ErrorHandlerDeps['isImpactBody']): boolean {
    if (error instanceof ApiError && error.status === 401) return true
    if (error instanceof ApiError && error.status === 409 && isImpactBody(error.errorDetail)) return true
    return false
}

function describeError(error: Error, serverUnavailableMessage: () => string): string {
    if (error.message === 'Failed to fetch') return serverUnavailableMessage()
    return error.message
}

/**
 * Build the mutationCache.onError handler. The factory takes the dependency
 * surface so tests can inject deterministic doubles.
 */
export function createMutationErrorHandler(deps: ErrorHandlerDeps) {
    return async function onMutationError(
        error: Error,
        mutationKey: readonly unknown[] | undefined,
        meta?: Readonly<Record<string, unknown>>,
    ): Promise<void> {
        // WHY: live-preview mutations (e.g. the certification scope preview) fire
        // on every keystroke and expect 400s for incomplete input; they surface
        // the error inline, so opt out of the global toast + error_logs mirror.
        if (meta?.suppressGlobalError === true) return
        const suppressToast = shouldSuppressToast(error, deps.isImpactBody)
        const description = describeError(error, deps.serverUnavailableMessage)

        // Backend already recorded this error AND told us the row id → link
        // straight to the backend entry (real Java stack), skip the duplicate
        // FrontendApiError mirror.
        if (error instanceof ApiError && error.backendErrorLogId) {
            if (!suppressToast) {
                deps.toast({
                    description,
                    errorLogUrl: routes.admin.errorLogs.detail(error.backendErrorLogId),
                })
            }
            return
        }

        // Otherwise: report the error to the backend, then toast with a link
        // to the resulting frontend record (or the list page if reporting failed).
        const errorLogId = error instanceof ApiError
            ? await reportApiError(error.requestUrl ?? '', error.status, error.code, error.message,
                error.errorDetail, error.requestHeaders, error.responseHeaders,
                mutationKey ? JSON.stringify(mutationKey) : undefined)
            : await reportJavaScriptError(error.message, undefined, (error as Error).stack)

        if (suppressToast) return

        deps.toast({
            description,
            errorLogUrl: errorLogId
                ? routes.admin.errorLogs.detail(errorLogId)
                : routes.admin.errorLogs.list(),
        })
    }
}

/**
 * Build the queryCache.onError reporter. Queries don't toast (the page renders
 * the error inline via ErrorBanner) — they just record the failure for ops.
 */
export function createQueryErrorReporter() {
    return function onQueryError(error: Error, queryKey: readonly unknown[]): Promise<string | null> | void {
        // Same dedup as mutation: skip the FrontendApiError mirror when the
        // backend already gave us its errorLogId.
        if (error instanceof ApiError && error.backendErrorLogId) {
            return
        }
        if (error instanceof ApiError) {
            return reportApiError(error.requestUrl ?? '', error.status, error.code, error.message,
                error.errorDetail, error.requestHeaders, error.responseHeaders,
                JSON.stringify(queryKey))
        }
        return reportJavaScriptError(error.message, undefined, (error as Error).stack)
    }
}
